// ============================================================
//  src/pages/bots/automations/AutomationsListPage.js
// ============================================================
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listAutomations, createAutomation,
  deleteAutomation, duplicateAutomation,
  activateAutomation, deactivateAutomation,
} from '../../../api/client';
import './Automations.css';

const TRIGGER_META = {
  whatsapp_message: { icon: '💬', label: 'WhatsApp Message',  color: '#25D366' },
  whatsapp_button:  { icon: '🔘', label: 'Button Reply',      color: '#007acc' },
  schedule:         { icon: '🕐', label: 'Schedule',          color: '#f59e0b' },
  webhook:          { icon: '🔗', label: 'Incoming Webhook',  color: '#8b5cf6' },
  manual:           { icon: '▶',  label: 'Manual',            color: '#6b7280' },
  payment_received: { icon: '💰', label: 'Payment Received',  color: '#00C88C' },
};

function timeAgo(dt) {
  if (!dt) return 'Never';
  const d = new Date(dt.replace(' ', 'T'));
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60)    return 'just now';
  if (s < 3600)  return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

// ── New Automation Modal ──────────────────────────────────────
function NewAutomationModal({ bId, onClose, onCreated }) {
  const TRIGGER_TYPES = [
    { key: 'whatsapp_message', icon: '💬', title: 'WhatsApp Message',  desc: 'Runs when a user sends a message matching a keyword' },
    { key: 'whatsapp_button',  icon: '🔘', title: 'Button Reply',      desc: 'Runs when a user taps a specific button' },
    { key: 'schedule',         icon: '🕐', title: 'Schedule',          desc: 'Runs on a recurring schedule (hourly, daily…)' },
    { key: 'webhook',          icon: '🔗', title: 'Incoming Webhook',  desc: 'Runs when an external system POSTs to a unique URL' },
    { key: 'manual',           icon: '▶',  title: 'Manual Trigger',    desc: 'Run manually from the dashboard for testing' },
    { key: 'payment_received', icon: '💰', title: 'Payment Received',  desc: 'Runs after a customer successfully pays a Request Payment button' },
  ];

  const [name,        setName]        = useState('');
  const [triggerType, setTriggerType] = useState('whatsapp_message');
  const [saving,      setSaving]      = useState(false);

  async function handleCreate() {
    if (!name.trim()) { toast.error('Give this automation a name'); return; }
    setSaving(true);
    try {
      const res = await createAutomation({ bId, name: name.trim(), triggerType });
      if (res.data?.success) {
        onCreated(res.data.aId);
      } else { toast.error(res.data?.message ?? 'Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">✨ New Automation</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Automation name</label>
            <input className="form-input" placeholder="e.g. Lead Capture, Order Confirmation…"
              value={name} onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">How does it start?</label>
            <div className="am-trigger-grid">
              {TRIGGER_TYPES.map(t => (
                <button key={t.key} type="button"
                  className={`am-trigger-opt${triggerType === t.key ? ' is-selected' : ''}`}
                  onClick={() => setTriggerType(t.key)}
                >
                  <span className="am-trigger-icon">{t.icon}</span>
                  <div className="am-trigger-text">
                    <div className="am-trigger-title">{t.title}</div>
                    <div className="am-trigger-desc">{t.desc}</div>
                  </div>
                  {triggerType === t.key && <span className="am-trigger-check">✓</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving || !name.trim()}>
            {saving ? <><span className="spinner"/> Creating…</> : 'Create & Open Builder →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Automation Card ───────────────────────────────────────────
function AutomationCard({ automation, bId, onEdit, onRefresh }) {
  const [acting, setActing] = useState(false);
  const meta    = TRIGGER_META[automation.aTriggerType] ?? TRIGGER_META.manual;
  const active  = automation.aStatus === 'active';

  async function toggle() {
    setActing(true);
    try {
      if (active) {
        await deactivateAutomation(bId, automation.aId);
        toast.success('Automation paused');
      } else {
        await activateAutomation(bId, automation.aId);
        toast.success('Automation activated!');
      }
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Failed');
    }
    setActing(false);
  }

  async function handleDuplicate() {
    setActing(true);
    try {
      await duplicateAutomation(bId, automation.aId);
      toast.success('Duplicated');
      onRefresh();
    } catch { toast.error('Failed'); }
    setActing(false);
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${automation.aName}"? This cannot be undone.`)) return;
    setActing(true);
    try {
      await deleteAutomation(bId, automation.aId);
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
    setActing(false);
  }

  return (
    <div className={`am-card${active ? ' am-card-active' : ''}`}>
      {/* Status stripe */}
      <div className="am-card-stripe" style={{ background: active ? '#00c88c' : '#e5e7eb' }} />

      <div className="am-card-body">
        {/* Header */}
        <div className="am-card-header">
          <div className="am-card-trigger-badge" style={{ background: meta.color + '18', color: meta.color }}>
            <span>{meta.icon}</span> {meta.label}
          </div>
          <div className={`am-status-pill${active ? ' am-status-active' : ' am-status-draft'}`}>
            <span className="am-status-dot" />
            {active ? 'Active' : 'Draft'}
          </div>
        </div>

        {/* Name & description */}
        <div className="am-card-name">{automation.aName}</div>
        {automation.aDescription && (
          <div className="am-card-desc">{automation.aDescription}</div>
        )}

        {/* Stats */}
        <div className="am-card-stats">
          <span>🔁 {automation.aRunCount} runs</span>
          <span className="am-stat-sep">·</span>
          <span>Last run: {timeAgo(automation.aLastRunAt)}</span>
          <span className="am-stat-sep">·</span>
          <span>Updated {timeAgo(automation.aUpdatedAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="am-card-actions">
        <button className="btn btn-outline btn-sm" onClick={onEdit}>✏️ Edit</button>
        <button
          className={`btn btn-sm${active ? ' btn-outline' : ' btn-primary'}`}
          onClick={toggle}
          disabled={acting}
        >
          {acting ? <span className="spinner spinner-dark"/> : (active ? '⏸ Pause' : '▶ Activate')}
        </button>
        <button className="icon-btn" title="Duplicate" onClick={handleDuplicate} disabled={acting}>⎘</button>
        <button className="icon-btn danger" title="Delete" onClick={handleDelete} disabled={acting}>🗑</button>
      </div>
    </div>
  );
}

// ── Main List Page ────────────────────────────────────────────
export default function AutomationsListPage({ bId }) {
  const navigate           = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showNew,     setShowNew]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAutomations(bId);
      setAutomations(res.data?.automations ?? []);
    } catch { toast.error('Failed to load automations'); }
    setLoading(false);
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  function handleCreated(aId) {
    setShowNew(false);
    navigate(`/bots/${bId}/automations/${aId}`);
  }

  const active  = automations.filter(a => a.aStatus === 'active');
  const drafts  = automations.filter(a => a.aStatus !== 'active');

  return (
    <div className="tab-content">
      {/* Toolbar */}
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Automations</div>
          <div className="tab-sub">
            No-code visual flows — connect triggers, actions, and integrations
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          + New Automation
        </button>
      </div>

      {/* Hero — empty state */}
      {!loading && automations.length === 0 && (
        <div className="am-empty">
          <div className="am-empty-graphic">
            <div className="am-empty-node am-empty-node-1">💬 Message</div>
            <div className="am-empty-arrow">→</div>
            <div className="am-empty-node am-empty-node-2">🔀 Branch</div>
            <div className="am-empty-arrow">→</div>
            <div className="am-empty-node am-empty-node-3">📧 Email</div>
          </div>
          <div className="am-empty-title">Build your first automation</div>
          <div className="am-empty-desc">
            Connect triggers, actions, and integrations visually — no code needed.
            Works with WhatsApp, email, HTTP, Google Sheets, Slack, and more.
          </div>
          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => setShowNew(true)}>
            ✨ Create Automation
          </button>
        </div>
      )}

      {/* Automation cards */}
      {loading ? (
        <div className="tab-loading"><span className="spinner spinner-dark"/> Loading…</div>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <div className="am-section-label">🟢 Active</div>
              <div className="am-list">
                {active.map(a => (
                  <AutomationCard key={a.aId} automation={a} bId={bId}
                    onEdit={() => navigate(`/bots/${bId}/automations/${a.aId}`)}
                    onRefresh={load} />
                ))}
              </div>
            </>
          )}
          {drafts.length > 0 && (
            <>
              <div className="am-section-label" style={{ marginTop: active.length ? 28 : 0 }}>
                ⚪ Drafts
              </div>
              <div className="am-list">
                {drafts.map(a => (
                  <AutomationCard key={a.aId} automation={a} bId={bId}
                    onEdit={() => navigate(`/bots/${bId}/automations/${a.aId}`)}
                    onRefresh={load} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {showNew && (
        <NewAutomationModal bId={bId} onClose={() => setShowNew(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}